import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fundraisingData = [
  { name: 'Week 1', amount: 0 },
  { name: 'Week 2', amount: 0 },
  { name: 'Week 3', amount: 0 },
  { name: 'Week 4', amount: 0 },
  { name: 'Week 5', amount: 0 },
];

const totalRaised = fundraisingData.reduce((sum, item) => sum + item.amount, 0);

const HomePage: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  
  // Update scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* Fixed Banner Background - responsive for mobile */}
      <div 
        className="fixed top-0 left-0 right-0 w-full h-[50vh] md:h-screen z-0 lg:ml-64" 
      >
        <div 
          className="h-full w-full rounded-lg lg:rounded-2xl overflow-hidden m-0 lg:m-4 shadow-lg flex justify-center lg:justify-start items-start lg:items-center pt-12 lg:pt-0"
          style={{
            backgroundColor: '#FFFBEB',
            position: 'relative'
          }}
        >
          {/* Use img tag with responsive sizing */}
          <img 
            src="/Banner.png" 
            alt="Besties Lemonade Banner"
            className="object-contain md:object-fill max-w-full max-h-full pt-0 pb-6 px-2 md:px-6 rounded-md"
            style={{
              width: 'auto',
              height: 'auto',
              maxHeight: '90%',
              borderRadius: '5px',
              transform: 'scale(1.10)',
              marginLeft: '50px',
              marginTop: '5px'
            }}
          />
          
          {/* Scroll indicator that disappears when scrolled - hidden on mobile */}
          <motion.div 
            className="absolute bottom-24 md:bottom-28 left-0 right-0 hidden md:flex flex-col items-center justify-center text-center w-full"
            initial={{ opacity: 1 }}
            animate={{ 
              opacity: scrollY > 50 ? 0 : 1,
              y: [0, 10, 0]
            }}
            transition={{ 
              opacity: { duration: 0.3 },
              y: { repeat: Infinity, duration: 1.5 }
            }}
          >
            <p className="text-white text-base md:text-lg font-bold bg-black bg-opacity-50 px-4 py-1 rounded-full mb-2">
              Scroll Down for More!
            </p>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <svg 
                className="w-8 h-8 md:w-10 md:h-10 text-yellow-500 fill-current"
                viewBox="0 0 20 20"
              >
                <path d="M10 15l-5.878-5.878a1 1 0 0 1 1.414-1.414L10 12.172l4.464-4.464a1 1 0 0 1 1.414 1.414L10 15z" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Spacer to allow content to start after viewing the banner */}
      <div className="h-[30vh] md:h-screen w-full" />

      {/* Content that scrolls over the banner */}
      <div className="relative z-10">
        <div className="bg-white bg-opacity-90 rounded-t-3xl shadow-xl">
          <div className="container mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8 md:space-y-12">
            {/* Our Story Section */}
            <motion.div 
              className="card"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-baloo text-yellow-500 mb-3 md:mb-4">Our Story</h2>
              <p className="text-lg md:text-xl font-comic">
                We're four best friends who love having fun, making treats, and helping our neighborhood stay cool on hot days. 
                We started Besties Lemonade because we wanted to spend more time together and raise money for charity! Half of all our proceeds 
                go to SOS Children Canada to help kids in need worldwide. When you buy from us, you're not just getting delicious lemonade - 
                you're helping children around the world!
              </p>
            </motion.div>

            {/* Infographic Section */}
            <motion.div 
              className="card"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-2xl md:text-3xl font-baloo text-yellow-500 mb-3 md:mb-4">How Much We've Raised So Far!</h2>
              
              <div className="mb-4 text-center">
                <span className="text-4xl md:text-5xl font-baloo font-bold text-pink-500">${totalRaised}</span>
                <span className="text-lg md:text-xl ml-2">total raised</span>
              </div>
              
              <div className="h-48 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={fundraisingData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                    <YAxis tick={{ fontSize: 14 }} />
                    <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
                    <Bar 
                      dataKey="amount" 
                      fill="#FDE047" 
                      radius={[10, 10, 0, 0]}
                      animationDuration={1500}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 